import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../../hooks';
import { IMAGES } from '../../../../assets';
import styles from './ScreenshotsSection.module.css';

/**
 * ScreenshotsSection Component (Landing Feature)
 * 
 * Screenshots section component for the landing page featuring:
 * - Simple horizontal strip of thumbnails (full image visible within box)
 * - Lightweight, accessible lightbox/modal for viewing
 * - Keyboard navigation (ESC close, Left/Right navigate)
 * - Focus management and body scroll lock
 * - CSS Modules for scoped styling
 * 
 * @component
 * @example
 * return (
 *   <ScreenshotsSection />
 * )
 */
const ScreenshotsSection = () => {
  const { t, language } = useTranslation();

  // Screenshot data for ImageGallery
  const screenshots = [
    {
      id: 'home',
      src: IMAGES.screenshots.home,
      alt: t.download.screenshots.homepage,
      title: t.download.screenshots.homepage,
      description: language === 'ar' ? 'الصفحة الرئيسية للتطبيق' : 'App homepage view',
    },
    {
      id: 'news',
      src: IMAGES.screenshots.news,
      alt: t.download.screenshots.news,
      title: t.download.screenshots.news,
      description: language === 'ar' ? 'صفحة الأخبار والإعلانات' : 'News and announcements page',
    },
    {
      id: 'bills',
      src: IMAGES.screenshots.bills,
      alt: t.download.screenshots.bills,
      title: t.download.screenshots.bills,
      description: language === 'ar' ? 'صفحة الفواتير والمدفوعات' : 'Bills and payments page',
    },
    {
      id: 'contact',
      src: IMAGES.screenshots.contact,
      alt: t.download.screenshots.contact,
      title: t.download.screenshots.contact,
      description: language === 'ar' ? 'صفحة التواصل مع البلدية' : 'Contact municipality page',
    },
  ];

  // Lightbox state
  const [openIndex, setOpenIndex] = useState(null);
  const closeBtnRef = useRef(null);
  const lastFocusRef = useRef(null);

  const openModal = (index) => {
    lastFocusRef.current = document.activeElement;
    setOpenIndex(index);
  };
  const closeModal = () => {
    setOpenIndex(null);
    // Restore focus to the last focused element
    setTimeout(() => {
      lastFocusRef.current?.focus?.();
    }, 0);
  };
  const showPrev = (e) => {
    e?.stopPropagation?.();
    setOpenIndex((idx) => (idx - 1 + screenshots.length) % screenshots.length);
  };
  const showNext = (e) => {
    e?.stopPropagation?.();
    setOpenIndex((idx) => (idx + 1) % screenshots.length);
  };

  // Ensure a portal root exists for the modal
  const modalRootRef = useRef(null);
  useEffect(() => {
    let el = document.getElementById('modal-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'modal-root';
      document.body.appendChild(el);
    }
    modalRootRef.current = el;
  }, []);

  // Lock body scroll and keyboard navigation, and hide background content
  useEffect(() => {
    if (openIndex === null) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Hide app content from assistive tech and interaction
    const appRoot = document.getElementById('root');
    const prevAriaHidden = appRoot?.getAttribute('aria-hidden');
    if (appRoot) {
      appRoot.setAttribute('aria-hidden', 'true');
      try { appRoot.inert = true; } catch {}
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    document.addEventListener('keydown', onKeyDown);
    // Focus close button for accessibility
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
      if (appRoot) {
        if (prevAriaHidden === null) appRoot.removeAttribute('aria-hidden');
        else appRoot.setAttribute('aria-hidden', prevAriaHidden);
        try { appRoot.inert = false; } catch {}
      }
    };
  }, [openIndex]);

  return (
    <section 
      className={styles.screenshots} 
      id="screenshots"
      aria-labelledby="screenshots-title"
    >
      <div className={styles.container}>
        <h3 id="screenshots-title" className={styles.title}>
          {t.download.appPreview}
        </h3>
        
        <div className={styles.strip} role="list" aria-label={language === 'ar' ? 'لقطات التطبيق' : 'App screenshots'}>
          {screenshots.map((shot, idx) => (
            <button
              key={shot.id}
              type="button"
              className={styles.card}
              onClick={() => openModal(idx)}
              aria-label={(shot.alt || shot.title) + (language === 'ar' ? ' - تكبير' : ' - enlarge')}
            >
              <div className={styles.thumbBox}>
                <img
                  src={shot.src}
                  alt={shot.alt || shot.title}
                  loading="lazy"
                  className={styles.thumb}
                />
              </div>
              <span className={styles.caption}>{shot.title}</span>
            </button>
          ))}
        </div>

        {openIndex !== null && modalRootRef.current && createPortal(
          <div className={styles.modalOverlay} onClick={closeModal} role="dialog" aria-modal="true">
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <button
                ref={closeBtnRef}
                className={styles.closeBtn}
                onClick={closeModal}
                aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
              >
                ✕
              </button>

              {screenshots.length > 1 && (
                <>
                  <button
                    className={`${styles.navBtn} ${styles.prevBtn}`}
                    onClick={showPrev}
                    aria-label={language === 'ar' ? 'السابق' : 'Previous'}
                  >
                    ‹
                  </button>
                  <button
                    className={`${styles.navBtn} ${styles.nextBtn}`}
                    onClick={showNext}
                    aria-label={language === 'ar' ? 'التالي' : 'Next'}
                  >
                    ›
                  </button>
                </>
              )}

              <img
                src={screenshots[openIndex].src}
                alt={screenshots[openIndex].alt || screenshots[openIndex].title}
                className={styles.modalImage}
                onClick={showNext}
              />

              {(screenshots[openIndex].title || screenshots[openIndex].description) && (
                <div className={styles.modalInfo}>
                  {screenshots[openIndex].title && (
                    <h3 className={styles.modalTitle}>{screenshots[openIndex].title}</h3>
                  )}
                  {screenshots[openIndex].description && (
                    <p className={styles.modalDescription}>{screenshots[openIndex].description}</p>
                  )}
                </div>
              )}

              {screenshots.length > 1 && (
                <div className={styles.counter} aria-live="polite">
                  {openIndex + 1} / {screenshots.length}
                </div>
              )}
            </div>
          </div>,
          modalRootRef.current
        )}
      </div>
    </section>
  );
};

export default ScreenshotsSection;